import { Button } from "@deligoo/ui"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"

import { useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { Clipboard } from "@/components/Clipboard"
import { ContextMenu, openContextMenu } from "@/components/ContextMenu"
import { Table } from "@/components/Table"
import { usePaymentsContextMenuTrigger } from "@/modules/PaymentContextMenu"
import { selectUnsettledPayments, selectUnsettledPaymentsStatus } from "@/store/payments"
import { selectCurrentTeamId } from "@/store/teams"
import { Payment } from "@/types/Payments"
import { fetchSyncPayments, formatPrice, truncate } from "@/utils"

import cls from "../PaymentsView.module.scss"

const TABLE_HEADERS = ["settlement_code", "datetime", "amount", "description", "driver"] as const

export const UnsettledPayments = () => {
  const teamId = useAppSelector(selectCurrentTeamId)
  const unsettledPayments = useAppSelector(selectUnsettledPayments)
  const unsettledPaymentsStatus = useAppSelector(selectUnsettledPaymentsStatus)

  const { t } = useTranslation("payments")

  async function syncPayments() {
    const toastOptions = {
      toastId: "syncPayments",
      updateId: "syncPayments",
    }

    try {
      if (!teamId) return

      const { response } = await fetchSyncPayments({ team_id: teamId })

      if (response.ok) {
        toast.success(t("payments_sync_success"), toastOptions)
      }
    } catch (error) {
      toast.success(t("payments_sync_fail"), toastOptions)
    }
  }

  return (
    <Card box className="mt-6">
      <Card.Header className={cls.settlementsHeader}>
        Rozliczenie bez podanego nr zamówienia
        <Button color="primary" onClick={syncPayments}>
          Synchronizuj płatności
        </Button>
      </Card.Header>

      <Table
        minWidth="1200px"
        columns="repeat(6, minmax(200px, 1fr)"
        loading={unsettledPaymentsStatus === "loading"}
        error={unsettledPaymentsStatus === "error"}
        empty={!unsettledPayments || unsettledPayments.length === 0}
      >
        <Table.Thead>
          <Table.Tr>
            {TABLE_HEADERS.map((header) => (
              <Table.Th key={header}>{t(`unsettled_payments_list_headers.${header}`)}</Table.Th>
            ))}
            <Table.Th />
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody>
          {unsettledPayments?.map((payment) => (
            // TODO: virtualized
            <UnsettledPaymentItem payment={payment} key={payment.id} />
          ))}
        </Table.Tbody>
      </Table>
    </Card>
  )
}

const UnsettledPaymentItem = ({ payment }: { payment: Payment }) => {
  const contextMenuRef = usePaymentsContextMenuTrigger({ paymentId: payment.id })

  const { t } = useTranslation("payments")

  return (
    <Table.Tr ref={contextMenuRef}>
      <Table.Td>
        <Clipboard>{payment.transaction_code}</Clipboard>
      </Table.Td>

      <Table.Td>{dayjs(payment.created_at).format("DD.MM.YYYY HH:mm")}</Table.Td>

      <Table.Td>{formatPrice(payment.amount)} zł</Table.Td>

      <Table.Td>
        {payment.product_summary && payment.product_summary.length > 0
          ? truncate(payment.product_summary, 25)
          : t(`payment_kinds.${payment.kind}`)}
      </Table.Td>

      <Table.Td>
        <Clipboard className="truncate">{payment.user}</Clipboard>
      </Table.Td>

      <Table.Td className="text-right">
        <ContextMenu.Button onClick={openContextMenu} />
      </Table.Td>
    </Table.Tr>
  )
}
